import { useCallback, useEffect, useRef } from 'react';
import { dismissToast, emitMessageToast } from '@adv-frontend/toast';
import { Toast, type ToastProps } from '../../../components/Toast';
import { getRequestContext } from '../../requestContext';
import styles from './useMessageToast.module.css';

export const TOAST_TIMEOUT = 5000;

interface UseMessageToastOptions {
    position?: 'top-center' | 'bottom-right';
}

interface ShowToastParams extends Pick<ToastProps, 'title' | 'caption'> {
    showOptions?: UseMessageToastOptions;
}

export const useMessageToast = (options: UseMessageToastOptions = {}) => {
    const { platform } = getRequestContext();
    const toastIdRef = useRef<string | undefined>();
    const timerIdRef = useRef<ReturnType<typeof setTimeout> | undefined>();

    const defaultPosition = platform === 'touch' ? 'top-center' : 'bottom-right';

    const showToast = useCallback(
        ({ title, caption, showOptions }: ShowToastParams) => {
            if (toastIdRef.current) {
                return;
            }

            const toast = emitMessageToast(
                Toast({
                    title,
                    caption,
                    onClose: () => {
                        dismissToast(toastIdRef.current);
                        toastIdRef.current = undefined;
                    },
                }),
                {
                    durationMs: TOAST_TIMEOUT,
                    position: showOptions?.position ?? options.position ?? defaultPosition,
                    className: styles.toast,
                }
            );

            toastIdRef.current = toast;
            timerIdRef.current = setTimeout(() => {
                toastIdRef.current = undefined;
            }, TOAST_TIMEOUT);
        },
        [options.position, defaultPosition]
    );

    useEffect(
        () => () => {
            if (timerIdRef.current) {
                clearTimeout(timerIdRef.current);
            }
        },
        []
    );

    return { showToast };
};
